import { Chart } from "chart.js/auto";
import { useState, useEffect, useRef } from "react";
import { COLORS } from "../../constants/colors";
import { Users, ChevronDown, TrendingUp, TrendingDown, User as UserIcon } from "lucide-react";
import { api } from "../../api";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(ChartDataLabels);

export default function UserOverTimeChart({ userData }) {
    const userOverTimeChartRef = useRef(null);
    const dropdownRef = useRef(null);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState("week");
    const [allMetrics, setAllMetrics] = useState(null);
    const [displayedMetrics, setDisplayedMetrics] = useState(null);
    const [totalUsers, setTotalUsers] = useState(null);
    const [growthPercentage, setGrowthPercentage] = useState(null);
    const [growthSinceDate, setGrowthSinceDate] = useState(null);
    const [selectedOption, setSelectedOption] = useState("Week");
    const [loading, setLoading] = useState(true);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const options = ["Week", "Month", "Year"];

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        setTimeRange(option.toLowerCase());
        filterDataByTimeRange(option.toLowerCase());
        setDropdownOpen(false);
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const calculateGrowthPercentage = (data, range) => {
        if (!data || data.length < 2) return { percentage: null, sinceDate: null };
        
        // Sort data by date
        const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        // Get current count (most recent data point)
        const currentCount = sortedData[sortedData.length - 1].count;
        
        // Get previous count based on time range
        let previousCount;
        const now = new Date();
        const earliestDate = new Date(sortedData[0].date);
        let sinceDate = null;
        
        switch (range) {
            case 'week':
                // Get count from 7 days ago or earliest available
                const weekAgo = new Date();
                weekAgo.setDate(now.getDate() - 7);
                
                // If we don't have data from a week ago, use earliest data point
                if (earliestDate > weekAgo) {
                    previousCount = sortedData[0].count;
                    sinceDate = earliestDate;
                } else {
                    // Find the closest data point to a week ago
                    const closestPoint = sortedData.reduce((closest, point) => {
                        const pointDate = new Date(point.date);
                        const closestDate = new Date(closest.date);
                        return Math.abs(pointDate - weekAgo) < Math.abs(closestDate - weekAgo) ? point : closest;
                    });
                    previousCount = closestPoint.count;
                    sinceDate = new Date(closestPoint.date);
                }
                break;
                
            case 'month':
                // Get count from a month ago or earliest available
                const monthAgo = new Date();
                monthAgo.setMonth(now.getMonth() - 1);
                
                if (earliestDate > monthAgo) {
                    previousCount = sortedData[0].count;
                    sinceDate = earliestDate;
                } else {
                    const closestPoint = sortedData.reduce((closest, point) => {
                        const pointDate = new Date(point.date);
                        const closestDate = new Date(closest.date);
                        return Math.abs(pointDate - monthAgo) < Math.abs(closestDate - monthAgo) ? point : closest;
                    });
                    previousCount = closestPoint.count;
                    sinceDate = new Date(closestPoint.date);
                }
                break;
                
            case 'year':
                // Get count from a year ago or earliest available
                const yearAgo = new Date();
                yearAgo.setFullYear(now.getFullYear() - 1);
                
                if (earliestDate > yearAgo) {
                    previousCount = sortedData[0].count;
                    sinceDate = earliestDate;
                } else {
                    const closestPoint = sortedData.reduce((closest, point) => {
                        const pointDate = new Date(point.date);
                        const closestDate = new Date(closest.date);
                        return Math.abs(pointDate - yearAgo) < Math.abs(closestDate - yearAgo) ? point : closest;
                    });
                    previousCount = closestPoint.count;
                    sinceDate = new Date(closestPoint.date);
                }
                break;
                
            default:
                previousCount = sortedData[0].count;
        }
        
        // Calculate percentage change
        if (previousCount === 0) return { percentage: currentCount > 0 ? 100 : 0, sinceDate };
        
        const percentageChange = ((currentCount - previousCount) / previousCount) * 100;
        return { percentage: parseFloat(percentageChange.toFixed(1)), sinceDate };
    };

    const filterDataByTimeRange = (range) => {
        if (!allMetrics?.data) return;

        const now = new Date();
        const cutoffDate = new Date();
        
        switch (range) {
            case 'week':
                cutoffDate.setDate(now.getDate() - 7);
                break;
            case 'month':
                cutoffDate.setMonth(now.getMonth() - 1);
                break;
            case 'year':
                cutoffDate.setFullYear(now.getFullYear() - 1);
                break;
            default:
                cutoffDate.setDate(now.getDate() - 7);
        }

        const filteredData = allMetrics.data.filter(item => 
            new Date(item.date) >= cutoffDate
        );

        setDisplayedMetrics({
            ...allMetrics,
            data: filteredData
        });
        
        // Calculate growth percentage
        const { percentage, sinceDate } = calculateGrowthPercentage(allMetrics.data, range);
        setGrowthPercentage(percentage);
        setGrowthSinceDate(sinceDate);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.accountId) {
                setError("No account ID available");
                setLoading(false);
                return;
            }

            try {
                const response = await api.get(`/api/metrics/users/${userData.accountId}/growth`);

                if (response?.users && Array.isArray(response.users)) {
                    const metricsData = {
                        data: response.users,
                        timestamp: new Date().toISOString()
                    };
                    
                    setAllMetrics(metricsData);
                    setTotalUsers(response.totalUsers);
                    
                    // Immediately filter and set displayed metrics
                    const now = new Date();
                    const cutoffDate = new Date();
                    cutoffDate.setDate(now.getDate() - 7);
                    
                    const filteredData = response.users.filter(item => 
                        new Date(item.date) >= cutoffDate
                    );
                    
                    setDisplayedMetrics({
                        ...metricsData,
                        data: filteredData
                    });
                    
                    // Calculate growth percentage for default week view
                    const { percentage, sinceDate } = calculateGrowthPercentage(response.users, 'week');
                    setGrowthPercentage(percentage);
                    setGrowthSinceDate(sinceDate);
                } else {
                    throw new Error("Invalid response format from metrics service");
                }
            } catch (err) {
                console.error("Error fetching user metrics:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userData?.accountId]);

    useEffect(() => {
        let chart = null;

        if (!loading && displayedMetrics?.data && displayedMetrics.data.length > 0 && userOverTimeChartRef.current) {
            // Destroy existing chart if it exists
            if (chart) {
                chart.destroy();
            }

            const formatLabel = (date) => {
                if (!date) return "No data";
                
                try {
                    const dateObj = new Date(date);
                    
                    // Check if date is valid
                    if (isNaN(dateObj.getTime())) {
                        console.error('Invalid date encountered:', date);
                        return "Invalid";
                    }
                    
                    if (timeRange === "week") {
                        return dateObj.toLocaleDateString("en-US", {
                            weekday: "short",
                            month: "short",
                            day: "numeric"
                        });
                    } else if (timeRange === "month") {
                        return dateObj.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric"
                        });
                    } else if (timeRange === "year") {
                        return dateObj.toLocaleDateString("en-US", {
                            month: "short",
                            year: "numeric"
                        });
                    }
                } catch (error) {
                    console.error('Error formatting date:', error);
                    return "Error";
                }
            };

            const data = displayedMetrics.data;
            
            // For year view, aggregate data by month to reduce noise
            let processedData = data;
            if (timeRange === "year") {
                const monthlyData = {};
                
                // Group by month
                data.forEach(item => {
                    const date = new Date(item.date);
                    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
                    
                    if (!monthlyData[monthKey]) {
                        monthlyData[monthKey] = {
                            date: new Date(date.getFullYear(), date.getMonth(), 1),
                            count: item.count,
                            dataPoints: 1
                        };
                    } else {
                        // Take the latest count for the month
                        if (new Date(item.date) > new Date(monthlyData[monthKey].date)) {
                            monthlyData[monthKey].count = item.count;
                        }
                    }
                });
                
                // Convert back to array and sort by date
                processedData = Object.values(monthlyData).sort((a, b) => 
                    new Date(a.date) - new Date(b.date)
                );
            }
            
            const counts = processedData.map(item => item.count);
            const minCount = Math.min(...counts);
            const maxCount = Math.max(...counts);
            const range = maxCount - minCount;

            // Check if we're on mobile (screen width < 640px to match gender chart)
            const isMobile = window.innerWidth < 640;

            chart = new Chart(userOverTimeChartRef.current, {
                type: "line",
                data: {
                    labels: processedData.map((item, index) => {
                        // For year view, show all month labels
                        if (timeRange === "year") {
                            return formatLabel(item.date);
                        }
                        
                        // On mobile, show fewer labels
                        if (isMobile) {
                            return index % (timeRange === "week" ? 2 : 3) === 0 ? formatLabel(item.date) : '';
                        }
                        
                        if (timeRange === "month" && processedData.length > 15) {
                            return index % 3 === 0 ? formatLabel(item.date) : '';
                        }
                        return formatLabel(item.date);
                    }),
                    datasets: [{
                        label: "Patrons",
                        data: counts,
                        fill: true,
                        borderColor: COLORS.PURPLE.hex,
                        backgroundColor: `${COLORS.PURPLE.hex}20`,
                        tension: 0,
                        pointRadius: timeRange === "year" ? (isMobile ? 4 : 5) : (isMobile ? 3 : 4),
                        pointHoverRadius: timeRange === "year" ? (isMobile ? 6 : 7) : (isMobile ? 5 : 6),
                        borderWidth: 2,
                        pointBackgroundColor: COLORS.PURPLE.hex,
                        pointBorderColor: '#fff',
                        pointBorderWidth: 1.5,
                        datalabels: {
                            display: false
                        }
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            left: isMobile ? 5 : 20,
                            right: isMobile ? 5 : 20,
                            top: isMobile ? 5 : 20,
                            bottom: isMobile ? 10 : 30
                        }
                    },
                    animation: {
                        duration: 1500, // Longer animation duration
                        easing: 'easeOutQuart', // More dramatic easing function
                        delay: (context) => {
                            // Stagger the animation of each data point
                            return context.dataIndex * 50;
                        }
                    },
                    transitions: {
                        active: {
                            animation: {
                                duration: 400
                            }
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        title: { 
                            display: true,
                            text: "Patron Growth",
                            font: {
                                size: isMobile ? 16 : 30,
                                family: "'Inter', sans-serif",
                                weight: 'bold'
                            },
                            padding: { 
                                top: 0,
                                bottom: isMobile ? 10 : 30
                            },
                            color: '#1e293b'
                        },
                        tooltip: {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            titleColor: '#000',
                            bodyColor: '#000',
                            borderColor: '#e2e8f0',
                            borderWidth: 1,
                            padding: isMobile ? 8 : 10,
                            titleFont: {
                                size: isMobile ? 11 : 14,
                                weight: 'bold',
                                family: "'Inter', sans-serif"
                            },
                            bodyFont: {
                                size: isMobile ? 11 : 13,
                                family: "'Inter', sans-serif"
                            },
                            displayColors: false,
                            callbacks: {
                                title: (tooltipItems) => {
                                    // Get the raw date from the data point, not the label
                                    const dataIndex = tooltipItems[0].dataIndex;
                                    const rawDate = processedData[dataIndex].date;
                                    return formatLabel(rawDate);
                                },
                                label: (context) => `Patrons: ${context.parsed.y}`
                            },
                            animation: {
                                duration: 200
                            }
                        },
                        datalabels: {
                            display: false
                        }
                    },
                    scales: {
                        x: {
                            grid: {
                                display: false
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                maxRotation: timeRange === "year" ? 45 : (isMobile ? 45 : 0),
                                minRotation: timeRange === "year" ? 45 : (isMobile ? 45 : 0),
                                font: {
                                    size: isMobile ? 10 : 14,
                                    family: "'Inter', sans-serif",
                                    weight: '600'
                                },
                                color: '#64748b',
                                padding: isMobile ? 2 : 8,
                                autoSkip: timeRange !== "year",
                                maxTicksLimit: timeRange === "year" ? 12 : undefined
                            },
                            // Add animation to the x-axis
                            animate: true
                        },
                        y: {
                            grid: {
                                display: false
                            },
                            border: {
                                display: false
                            },
                            // Always start from the minimum value in the dataset
                            // This prevents the line from dipping below the actual data
                            beginAtZero: false,
                            min: minCount > 0 ? minCount * 0.95 : 0, // Start slightly below min value
                            suggestedMax: maxCount + Math.ceil(range * 0.1),
                            ticks: {
                                stepSize: 1,
                                precision: 0,
                                font: {
                                    size: isMobile ? 10 : 14,
                                    family: "'Inter', sans-serif",
                                    weight: '600'
                                },
                                color: '#64748b',
                                padding: isMobile ? 5 : 10,
                                maxTicksLimit: isMobile ? 5 : 10,
                                callback: function(value) {
                                    if (Math.floor(value) === value) {
                                        return value;
                                    }
                                }
                            },
                            // Add animation to the y-axis
                            animate: true
                        }
                    },
                    interaction: {
                        intersect: false,
                        mode: 'index'
                    },
                    elements: {
                        line: {
                            borderJoinStyle: 'round'
                        },
                        point: {
                            // Add animation for points
                            hoverRadius: 8,
                            hoverBorderWidth: 2,
                            // Animate points on hover
                            hitRadius: 8
                        }
                    },
                    // Add hover animation
                    hover: {
                        mode: 'index',
                        intersect: false,
                        animationDuration: 300
                    }
                }
            });
        }

        return () => {
            if (chart) {
                chart.destroy();
            }
        };
    }, [displayedMetrics, loading, timeRange]);

    if (error) {
        return (
            <div className="metrics-container bg-white rounded-lg shadow-sm p-6 m-5">
                <div className="text-red-500 text-center p-5">
                    Error loading patrons: {error}
                </div>
            </div>
        );
    }
    
    if (totalUsers === 0) {
        return (
            <div className="flex flex-col items-center bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="flex flex-col md:flex-row md:items-center justify-between w-full p-4 md:p-6 border-b border-gray-100">
                    <div className="flex items-center text-gray-800 mb-4 md:mb-0">
                        <Users className="text-3xl md:text-4xl mr-3 md:mr-4 text-[#3B0764]" />
                        <div className="text-left">
                            <h2 className="text-lg md:text-xl font-semibold text-gray-700">Total Patrons</h2>
                            <p className="text-4xl md:text-5xl font-bold text-[#3B0764] mt-1">0</p>
                        </div>
                    </div>
                </div>
                
                <div className="w-full p-8 flex flex-col items-center justify-center" style={{ minHeight: "300px" }}>
                    <div className="text-center">
                        <UserIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Patrons Yet</h3>
                        <p className="text-gray-500 max-w-md">
                            You don't have any patrons yet. As people subscribe to your content, 
                            you'll see growth metrics and charts here.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center justify-between w-full p-4 md:p-6 border-b border-gray-100">
                <div className="flex items-center text-gray-800 mb-4 md:mb-0">
                    <Users className="text-3xl md:text-4xl mr-3 md:mr-4 text-[#3B0764]" />
                    <div className="text-left">
                        <div className="flex items-center">
                            <h2 className="text-lg md:text-xl font-semibold text-gray-700">Total Patrons</h2>
                            {growthPercentage !== null && (
                                <div className={`flex items-center ml-3 px-3 py-1.5 rounded-full text-sm md:text-base font-medium ${
                                    growthPercentage > 0 
                                        ? 'bg-green-100 text-green-800' 
                                        : growthPercentage < 0 
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {growthPercentage > 0 
                                        ? <TrendingUp className="w-4 h-4 mr-1.5" /> 
                                        : growthPercentage < 0 
                                            ? <TrendingDown className="w-4 h-4 mr-1.5" />
                                            : null
                                    }
                                    {Math.abs(growthPercentage)}% since {growthSinceDate ? growthSinceDate.toLocaleDateString() : ''}
                                </div>
                            )}
                        </div>
                        <p className="text-4xl md:text-5xl font-bold text-[#3B0764] mt-1">{totalUsers}</p>
                    </div>
                </div>
                <div className="flex items-center">
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={toggleDropdown}
                            className="flex items-center justify-between px-4 py-2 md:px-5 md:py-2.5 rounded-full text-sm font-medium bg-[#3B0764] text-white focus:outline-none focus:ring-2 focus:ring-purple-400 shadow-sm transition-all duration-300 hover:bg-[#4B0A7D] min-w-[100px]"
                        >
                            {selectedOption}
                            <ChevronDown 
                                className={`ml-2 h-4 w-4 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} 
                            />
                        </button>
                        
                        {/* Dropdown menu */}
                        <div 
                            className={`absolute right-0 mt-2 w-full bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 z-10 ${
                                dropdownOpen 
                                    ? 'opacity-100 transform translate-y-0 max-h-[200px]' 
                                    : 'opacity-0 transform -translate-y-2 max-h-0 pointer-events-none'
                            }`}
                        >
                            {options.map((option) => (
                                <div
                                    key={option}
                                    className={`px-4 py-2 cursor-pointer hover:bg-purple-50 transition-colors duration-150 ${
                                        option === selectedOption ? 'bg-purple-100 text-[#3B0764] font-medium' : 'text-gray-700'
                                    }`}
                                    onClick={() => handleOptionSelect(option)}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {displayedMetrics && (
                <div className="text-xs md:text-sm text-gray-500 w-full px-4 md:px-6 py-2 bg-gray-50 border-b border-gray-100">
                    Last updated: {new Date(displayedMetrics.timestamp).toLocaleString()}
                </div>
            )}

            <div className="w-full p-2 md:p-4" style={{ height: window.innerWidth < 768 ? "300px" : "400px" }}>
                <canvas ref={userOverTimeChartRef}></canvas>
            </div>
        </div>
    );
}
